/**
 * Pulls a human readable message out of whatever the api client rejected with.
 *
 * The response interceptor in `apiClient` rejects with `error.response.data`
 * rather than the axios error, so a `catch` block receives the server body
 * directly: `{message, errors?}`. Reading `error.response.data` there always
 * misses, which is why several call sites were falling back to a generic
 * "خطا در ..." string and hiding the real validation message.
 *
 * Laravel sends field errors under `errors` and a summary under `message`;
 * the field errors are preferred because they name what to fix.
 */
export const extractApiError = (error: unknown, fallback: string): string => {
  if (typeof error === 'string') {
    return error || fallback;
  }

  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const body = error as {
    message?: unknown;
    error?: unknown;
    errors?: Record<string, unknown>;
  };

  if (body.errors && typeof body.errors === 'object') {
    const messages = Object.values(body.errors)
      .flatMap((fieldErrors) =>
        Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors],
      )
      .filter((item): item is string => typeof item === 'string');

    if (messages.length > 0) {
      return messages.join('\n');
    }
  }

  if (typeof body.message === 'string' && body.message !== '') {
    return body.message;
  }

  // The auth middleware and getAuthenticatedUser answer with `error` rather
  // than `message`, a shape kept from the legacy panel.
  if (typeof body.error === 'string' && body.error !== '') {
    return body.error;
  }

  return fallback;
};

export default extractApiError;
